import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  Chip, 
  IconButton, 
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  PlayArrow as PlayIcon, 
  Stop as StopIcon,
  Videocam as CameraIcon,
  Schedule as ScheduleIcon,
  SportsTennis as CourtIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import CourtStreamingPanel from './CourtStreamingPanel';

const CourtDetailsModal = ({ 
  court, 
  onClose, 
  onStartStream,
  onStopStream, 
  isStreamActive, 
  streamingLoading,
  streamingData = {}
}) => {
  const { isAdmin } = useAuth();
  const [selectedCamera, setSelectedCamera] = useState(null);

  const handleStartStream = (camera) => {
    setSelectedCamera(camera);
    onStartStream(court, camera.rtspUrl);
  };

  const handleStopStream = () => {
    setSelectedCamera(null);
    onStopStream(court);
  };

  return (
    <Box className="space-y-6">
      {/* Court Header */}
      <Card>
        <CardContent>
          <Box className="flex justify-between items-start">
            <Box className="flex items-center">
              <CourtIcon className="text-accent-teal mr-3 text-3xl" />
              <Box>
                <Typography variant="h5" className="font-bold">
                  {court.courtName}
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  Court ID: {court.courtId}
                </Typography>
              </Box>
            </Box>
            <Box className="flex items-center gap-2">
              <Chip 
                label={isStreamActive ? 'Live Streaming' : 'Offline'} 
                color={isStreamActive ? 'success' : 'default'}
                icon={isStreamActive ? <PlayIcon /> : <StopIcon />}
              />
              <Tooltip title="Edit Court">
                <IconButton className="text-accent-teal">
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Court">
                <IconButton className="text-red-500">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Court Configuration */}
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4">
                Basic Information
              </Typography>
              <Box className="space-y-3">
                <Box>
                  <Typography variant="body2" className="text-gray-600">
                    Court Name
                  </Typography>
                  <Typography variant="body1" className="font-medium">
                    {court.courtName}
                  </Typography>
                </Box>
                {court.hlsUrl && (
                  <Box>
                    <Typography variant="body2" className="text-gray-600">
                      HLS URL
                    </Typography>
                    <Typography variant="body2" className="font-mono text-sm break-all">
                      {court.hlsUrl}
                    </Typography>
                  </Box>
                )}
                {court.streamKey && (
                  <Box>
                    <Typography variant="body2" className="text-gray-600">
                      Stream Key
                    </Typography>
                    <Typography variant="body2" className="font-mono text-sm">
                      {court.streamKey}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Streaming Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" className="mb-4">
                Streaming Status
              </Typography>
              <Box className="space-y-3">
                <Box className="flex items-center justify-between">
                  <Typography variant="body2">
                    Status
                  </Typography>
                  <Chip 
                    label={isStreamActive ? 'Active' : 'Inactive'} 
                    color={isStreamActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                {isStreamActive && (
                  <Box className="flex items-center justify-between">
                    <Typography variant="body2">
                      Active Camera
                    </Typography>
                    <Typography variant="body2" className="font-medium">
                      {selectedCamera?.cameraName || 'Unknown'}
                    </Typography>
                  </Box>
                )}
                <Box className="pt-2">
                  {isStreamActive ? (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={handleStopStream}
                      disabled={streamingLoading}
                      fullWidth
                    >
                      {streamingLoading ? <CircularProgress size={20} /> : 'Stop Stream'}
                    </Button>
                  ) : (
                    <Typography variant="body2" className="text-gray-600">
                      Select a camera below to start streaming
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cameras Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" className="mb-4 flex items-center">
            <CameraIcon className="mr-2" />
            Cameras ({court.cameras?.length || 0})
          </Typography>
          
          {!court.cameras || court.cameras.length === 0 ? (
            <Alert severity="info">
              No cameras configured for this court.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Camera Name</TableCell>
                    {isAdmin() && <TableCell>RTSP URL</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {court.cameras.map((camera, index) => (
                    <TableRow key={camera.id || index}>
                      <TableCell>
                        <Typography variant="body2" className="font-medium">
                          {camera.cameraName}
                        </Typography>
                      </TableCell>
                      {isAdmin() && (
                        <TableCell>
                          <Typography variant="body2" className="font-mono text-xs">
                            {camera.rtspUrl}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip 
                          label={camera.active ? 'Active' : 'Inactive'} 
                          color={camera.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box className="flex gap-1">
                          {!isStreamActive && (
                            <Tooltip title={`Start streaming ${camera.cameraName}`}>
                              <IconButton
                                size="small"
                                onClick={() => handleStartStream(camera)}
                                disabled={streamingLoading || !camera.active}
                                className="text-green-600"
                              >
                                <PlayIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isStreamActive && selectedCamera?.id === camera.id && (
                            <Tooltip title="Stop streaming">
                              <IconButton
                                size="small"
                                onClick={handleStopStream}
                                disabled={streamingLoading}
                                className="text-red-600"
                              >
                                <StopIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Live Streaming Panel */}
      <CourtStreamingPanel
        court={court}
        onStartStream={onStartStream}
        onStopStream={onStopStream}
        isStreamActive={isStreamActive}
        streamingLoading={streamingLoading}
        hlsUrl={streamingData.hlsUrl || ''}
        streamKey={streamingData.streamKey || ''}
      />

      {/* Sessions Section */}
      <Card>
        <CardContent>
          <Typography variant="h6" className="mb-4 flex items-center">
            <ScheduleIcon className="mr-2" />
            Court Sessions ({court.sessions?.length || 0})
          </Typography>
          
          {!court.sessions || court.sessions.length === 0 ? (
            <Alert severity="info">
              No sessions configured for this court.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Session Name</TableCell>
                    <TableCell>Day</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {court.sessions.map((session, index) => (
                    <TableRow key={session.id || index}>
                      <TableCell>
                        <Typography variant="body2" className="font-medium">
                          {session.sessionName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {session.dayOfWeek}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {session.startTime}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {session.endTime}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={session.active ? 'Active' : 'Inactive'} 
                          color={session.active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Owner Information */}
      {court.owner && (
        <Card>
          <CardContent>
            <Typography variant="h6" className="mb-4">
              Owner Information
            </Typography>
            <Box className="space-y-2">
              <Box className="flex justify-between">
                <Typography variant="body2" className="text-gray-600">
                  Owner ID
                </Typography>
                <Typography variant="body2">
                  {court.owner.id}
                </Typography>
              </Box>
              {court.owner.username && (
                <Box className="flex justify-between">
                  <Typography variant="body2" className="text-gray-600">
                    Username
                  </Typography>
                  <Typography variant="body2">
                    {court.owner.username}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CourtDetailsModal;
